import { Controller } from "@hotwired/stimulus";

// "Repeat weekly through [date]" section of the Add Occurrence modal.
//
// Collapsed: the form is the plain single-date add (posts to sub_events#create).
// Expanded with a valid through-date: fetches a live server-side preview
// (sub_events#bulk_preview — the same resolver bulk_create uses, so the count
// shown is the count created), relabels the primary button to
// "Generate N Occurrences" and points the form at bulk_create. Empty range /
// over-cap states disable the button with an explanatory message.
//
// Flexible weekdays (card 262 follow-up): the day targets are the weekday
// chips — checked days ride along in the preview fetch as days[]; the create
// submit needs no JS because the chips are real checkboxes inside the form.
// Zero days checked is resolved client-side (no round-trip): explain + disable.
//
// Design polish (card 262 follow-up): a countable preview no longer renders as
// one green line — it populates three blocks from the same response:
//   - the impact list under the "Extend this recurring series" checkbox,
//   - the "Series Pass Impact" info banner (count woven into the copy;
//     the target only exists when the event has series-pass holders),
//   - the Summary card (count + first/last new date, from preview `dates`).
// All three hide again whenever the preview is blocked or cleared.
//
// Connects to data-controller="subevent-bulk-generate"
export default class extends Controller {
  static targets = ["form", "section", "toggle", "chevron", "throughDate", "preview", "submit", "day",
                    "impact", "impactOccurrences", "passImpact", "passImpactBody",
                    "summary", "summaryCount", "summarySkipped", "summaryFirstDate", "summaryLastDate"];
  static values = {
    previewUrl: String,
    createUrl: String,
    labels: Object
  };

  connect() {
    this.expanded = false;
    this.singleUrl = this.formTarget.action;
    this.singleLabel = this.submitTarget.value;
  }

  disconnect() {
    clearTimeout(this.debounce);
  }

  toggle() {
    this.expanded = !this.expanded;
    this.sectionTarget.classList.toggle("d-none", !this.expanded);
    this.toggleTarget.setAttribute("aria-expanded", this.expanded);
    this.chevronTarget.classList.toggle("expanded", this.expanded);

    if (this.expanded) {
      this.refresh();
    } else {
      this.singleMode();
    }
  }

  // Fired by any change inside the form wrapper (start date or through date —
  // flatpickr dispatches bubbling change events), debounced.
  scheduleRefresh() {
    if (!this.expanded) return;
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.refresh(), 300);
  }

  async refresh() {
    const startDate = this.startDateValueFromForm();
    const throughDate = this.throughDateTarget.value;

    // No through-date yet: the modal still saves the single date as today.
    if (!startDate || !throughDate) {
      this.showPreviewMessage("");
      this.singleMode();
      return;
    }

    // Every chip unchecked: nothing can be generated — say so without a
    // server round-trip (the server rejects this state too).
    const days = this.selectedDays();
    if (days.length === 0) {
      this.showPreviewMessage(this.labelsValue.daysRequired, "is-blocked");
      this.bulkMode(0, false);
      return;
    }

    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set("start_date", startDate);
    url.searchParams.set("through_date", throughDate);
    days.forEach((day) => url.searchParams.append("days[]", day));

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) {
        this.showPreviewMessage(this.labelsValue.noneInRange, "is-blocked");
        this.bulkMode(0, false);
        return;
      }
      this.render(await response.json());
    } catch {
      // Network hiccup: leave the previous state in place.
    }
  }

  render(data) {
    if (data.error) {
      this.showPreviewMessage(data.error, "is-blocked");
      this.bulkMode(0, false);
    } else if (data.over_cap) {
      this.showPreviewMessage(this.labelsValue.overCap, "is-blocked");
      this.bulkMode(0, false);
    } else if (data.count === 0) {
      this.showPreviewMessage(this.labelsValue.noneInRange, "is-blocked");
      this.bulkMode(0, false);
    } else {
      this.showPreviewMessage("");
      this.showImpactBlocks(data);
      this.bulkMode(data.count, true);
    }
  }

  // Blocked-state guidance line (empty range / over cap / no days picked).
  // Always hides the impact blocks first — they only describe a generatable
  // batch, and the success path re-shows them right after clearing this line.
  showPreviewMessage(text, state = "") {
    this.previewTarget.textContent = text;
    this.previewTarget.classList.toggle("is-blocked", state === "is-blocked");
    this.hideImpactBlocks();
  }

  // Populate the checkbox impact list, the Series Pass Impact banner and the
  // Summary card from one preview payload. `dates` is the resolved batch in
  // order, so first/last new date are its ends.
  showImpactBlocks({ count, skipped, dates }) {
    this.impactOccurrencesTarget.textContent =
      this.plural(count, this.labelsValue.impactOccurrencesOne, this.labelsValue.impactOccurrencesOther);
    this.impactTarget.classList.remove("d-none");

    if (this.hasPassImpactTarget) {
      this.passImpactBodyTarget.textContent =
        this.plural(count, this.labelsValue.passImpactOne, this.labelsValue.passImpactOther);
      this.passImpactTarget.classList.remove("d-none");
    }

    this.summaryCountTarget.textContent =
      this.plural(count, this.labelsValue.summaryCreatedOne, this.labelsValue.summaryCreatedOther);
    this.summarySkippedTarget.textContent = skipped > 0
      ? this.plural(skipped, this.labelsValue.previewSkippedOne, this.labelsValue.previewSkippedOther)
      : "";
    this.summarySkippedTarget.classList.toggle("d-none", skipped === 0);
    this.summaryFirstDateTarget.textContent = this.formatDate(dates[0]);
    this.summaryLastDateTarget.textContent = this.formatDate(dates[dates.length - 1]);
    this.summaryTarget.classList.remove("d-none");
  }

  hideImpactBlocks() {
    this.impactTarget.classList.add("d-none");
    if (this.hasPassImpactTarget) this.passImpactTarget.classList.add("d-none");
    this.summaryTarget.classList.add("d-none");
  }

  // ISO date (event-local calendar day) -> "Nov 3, 2026". Parsed as local
  // midnight on purpose: `new Date("2026-11-03")` alone is UTC midnight and
  // renders the previous day in negative-offset timezones.
  formatDate(iso) {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(document.documentElement.lang || "en-US", {
      month: "short", day: "numeric", year: "numeric"
    });
  }

  selectedDays() {
    return this.dayTargets.filter((chip) => chip.checked).map((chip) => chip.value);
  }

  plural(count, one, other) {
    return (count === 1 ? one : other).replace("%{count}", count);
  }

  // Primary button posts the whole form (dates + times + status + instructions
  // + through_date) to bulk_create.
  bulkMode(count, enabled) {
    this.formTarget.action = this.createUrlValue;
    this.submitTarget.disabled = !enabled;
    this.submitTarget.value = enabled
      ? this.plural(count, this.labelsValue.generateOne, this.labelsValue.generateOther)
      : this.singleLabel;
  }

  // Restore the plain single-date save.
  singleMode() {
    this.formTarget.action = this.singleUrl;
    this.submitTarget.disabled = false;
    this.submitTarget.value = this.singleLabel;
  }

  startDateValueFromForm() {
    const field = this.formTarget.querySelector("[name='subevent[date_local]']");
    return field ? field.value : null;
  }
}
